import { signal } from '@preact/signals';
import type {
  WidgetConfig,
  LeadField,
  PendingCards,
  LeadCardData as SharedLeadCardData,
} from '@digital-cs/shared';
import type { UIMessage, SupportedLocale } from '../types';
import type { VideoState } from '../services/video-state-machine';
import type { SessionHistoryItem } from '../services/ws-client';
import type { BubblePlacement } from '../services/widget-position-store';
import { dismissLeadCard } from '../services/ws-client';
import { createI18n } from '../i18n';

/** 当前需要展示的留资卡（lead.card.show 推送后置位；提交或 dismiss 后清空） */
export interface LeadCardData {
  fields: LeadField[];
  prompt: string;
  /**
   * 触发该卡片的 AI 回答消息 id（BE lead.card.show 的 messageId / 历史还原的 pendingCard.messageId）。
   * 渲染层据此把卡片锚定在该回答下方；缺省（旧 BE / 锚点消息不在当前可见列表）回落到消息流末尾。
   */
  anchorMsgId?: string;
}

export const config = signal<WidgetConfig | null>(null);
export const opened = signal<boolean>(false);

/**
 * 整页 H5 宿主模式:由宿主页 h5.html 的 `<script data-full-page="1">` 标记驱动(main.tsx bootstrap 置位)。
 * true 时:① 面板/沉浸层强制 100dvh 铺满整屏(任意宽度,见 :host[data-dcs-fullpage] CSS);
 *          ② 隐藏悬浮气泡 / 遮罩 / 关闭按钮(整页即客服,无处可"收起",退出交给小程序原生导航)。
 * 供 widget 内部展开逻辑与各组件(WebChatPanel / ImmersiveShell / WelcomePane / ChatPane)读取。
 */
export const fullPage = signal<boolean>(false);

/**
 * 当前 widget 的 clientId(嵌入 script 的 data-client-id;mock 模式占位 'mock')。
 * 迷你按钮拖动位置 / 提示冷却按它隔离缓存,bootstrap 时置位。
 */
export const widgetClientId = signal<string | null>(null);

/**
 * 迷你按钮「长按拖动」后的缓存落点(就近角锚定 + px 偏移);null 表示无缓存,
 * 按钮回落到服务端配置驱动的 CSS 变量。bootstrap 读 localStorage 初始化,落手时更新。
 * 仅作用于按钮本身(组件层 inline style),不影响 web 面板位置。
 */
export const draggedBubblePos = signal<BubblePlacement | null>(null);

/**
 * 打开后「框体」各自的拖动落点(就近角锚定 + px 偏移),与迷你气泡 draggedBubblePos 各记各的。
 * null 表示无缓存(或已过 24h 有效期),回落各自 CSS 默认布局:
 *  - webPanelPos:     网页面板「普通停靠态」;无落点时再回落「跟随气泡落点」computePanelStyle。
 *  - webPanelMaxPos:  网页面板「最大化态」;无落点时回落 CSS 居中(translate 50%)。
 *  - digitalModalPos: 数字人桌面弹窗;无落点时回落 .dcs-overlay flex 居中。
 * bootstrap 读 localStorage(按 clientId + slot 隔离,24h 过期)初始化,落手时由 useFrameDrag 更新。
 */
export const webPanelPos = signal<BubblePlacement | null>(null);
export const webPanelMaxPos = signal<BubblePlacement | null>(null);
export const digitalModalPos = signal<BubblePlacement | null>(null);

/**
 * 网页客服(web)面板是否最大化(居中大窗 .is-maximized)。
 * - 面板内「最大化/还原」「关闭」按钮、最大化遮罩点击 都读写它;
 * - 外部 window.DCSWidget.open({ maximized }) / 唤起事件 detail 也可控制。
 * digital 线不使用(展开即全屏沉浸 overlay)。
 */
export const webMaximized = signal<boolean>(false);

/**
 * 数字人客服(digital)线的三态视图机:
 *  - 'minimized' 仅悬浮球;'welcome' 欢迎屏;'chat' 聊天面板。
 * 与 opened 维持不变式: opened === (digitalView !== 'minimized')。
 * 视图态生命周期仅由 openDigitalWelcome / startDigitalChat / minimizeDigital 管理;
 * resetStore() 不动它(在 chat 里点重置应停留 chat,不跳回欢迎屏)。
 * web 线不使用本字段,仍只看 opened。
 */
export type DigitalViewState = 'minimized' | 'welcome' | 'chat';
export const digitalView = signal<DigitalViewState>('minimized');
export const messages = signal<UIMessage[]>([]);
export const currentLocale = signal<SupportedLocale>('zh');

/**
 * 数字人客服(digital)线当前接待模式:'ai' 走自家 AI 链路并出声;'human' 转人工接入 vocechat IM。
 * 常驻 DigitalRoot 读它决定是否把 ai.audio 入队播放/驱动视频;ChatPane 据它切 AI/人工 UI。
 * web 线不使用(WebChatPanel 自持局部 chatMode)。resetStore / minimizeDigital 归位为 'ai'。
 */
export const digitalChatMode = signal<'ai' | 'human'>('ai');

/** 数字人语音静音开关(用户在聊天头部喇叭按钮切换);true 时音频静音播放(仍按时长推进打字机)。 */
export const audioMuted = signal<boolean>(false);
/** 当前正在播放语音的 AI messageId(驱动打字机/高亮);空闲为 null。 */
export const audioPlayingId = signal<string | null>(null);
/** 浏览器 autoplay 是否已被用户手势解锁(首次进入聊天/发送时置位)。 */
export const audioUnlocked = signal<boolean>(false);
export const inputDraft = signal<string>('');
export const ready = signal<boolean>(false);
/** 一旦访客发出第一条消息就置 true,用以隐藏快速提问按钮区 */
export const quickQuestionsHidden = signal<boolean>(false);
/** 是否正在等待 AI 首字 (visitor.message 已发出但 ai.stream.start 还没到) */
export const aiThinking = signal<boolean>(false);
/** 数字人视频状态机当前状态 (Task 12);DigitalHumanVideo 订阅渲染 */
export const videoState = signal<VideoState>('idle');
/**
 * 数字人视频真实宽高比(videoWidth/videoHeight),由 DigitalHumanVideo 读元数据置位。
 * 桌面端 .dcs-dh-stage 据它收窄左框到刚好贴合视频(消除 pillarbox 留白);
 * null 表示尚未知,回落到固定 50% 框。移动端不消费(仍全屏 cover)。
 */
export const dhVideoAspect = signal<number | null>(null);
/**
 * 数字人入口(悬浮球)是否可显示:启动预载海报(poster-preload)落定后置 true。
 * 只门控手动入口;程序化唤起(DCSWidget.open / dcs:widget:open 事件 / fullPage
 * 自动展开 / defaultWindow=max)不经此信号,随时直接开 —— 小程序整页 H5 行为不变。
 */
export const digitalEntryReady = signal<boolean>(false);
/** 当前激活的留资卡（lead.card.show 推送置位；提交/关闭后清空） */
export const activeLeadCard = signal<LeadCardData | null>(null);

/**
 * 本会话最近一次推过的留资卡缓存,用于"dismiss 后再开"的入口还原。
 * lifecycle:
 *  - `lead.card.show` 推送 → 同步写入(跟 activeLeadCard 一起置位)
 *  - dismiss 不清(因为还需要再开)
 *  - submit 成功 → 清空 → 同时让所有 lead-reopen 系统消息渲染时返回 null,入口彻底消失
 *  - 会话重置(resetStore / handleResetChat) → 清空
 */
export const lastLeadCard = signal<LeadCardData | null>(null);

export function appendMessage(msg: UIMessage) {
  messages.value = [...messages.value, msg];
}

/**
 * 收起当前留资卡并在消息流末尾留「再开」入口 pill —— 手动 dismiss(LeadCaptureCard)
 * 与新回合自动收起(访客再发消息时)共用同一份逻辑,避免行为漂移。
 *
 * - 去重:先抹掉消息流里已有的 lead-reopen pill 再 append,全程只有一条入口;
 * - 通知 BE 把 pending_lead_card.dismissed 置 true,下次打开 widget 按 pill 还原而非直接弹卡;
 * - 卡片开着才动作,未开时调用是 no-op。
 */
export function closeLeadCardWithReopenPill(): void {
  if (!activeLeadCard.value) return;
  // 锚点先取出再清卡:pill 锚定到触发卡片的 AI 回答正下方,不再沉到消息流末尾
  const anchorMsgId = activeLeadCard.value.anchorMsgId;
  activeLeadCard.value = null;
  const filtered = messages.value.filter((m) => m.meta?.kind !== 'lead-reopen');
  messages.value = [
    ...filtered,
    {
      id: `m-lead-reopen-${Date.now()}`,
      role: 'system',
      text: createI18n(currentLocale.value).t('panel.leadReopenHint'),
      ts: Date.now(),
      meta: { kind: 'lead-reopen', anchorMsgId },
    },
  ];
  // 失败不阻塞 UI — 最差情况下次打开 widget 直接弹卡而非 pill,业务上仍可用。
  void dismissLeadCard();
}

/** 按 id 局部更新 message — 流式 end / streaming 状态切换用 */
export function updateMessage(id: string, patch: Partial<UIMessage>) {
  let touched = false;
  const next = messages.value.map((m) => {
    if (m.id !== id) return m;
    touched = true;
    return { ...m, ...patch };
  });
  if (touched) messages.value = next;
}

/**
 * 流式追加 delta。如果对应 id 的 message 不存在 (start 漏到),
 * 则补建一个 streaming AI 气泡 — 让 UI 永远不会丢字。
 */
export function appendDelta(id: string, delta: string) {
  let touched = false;
  const next = messages.value.map((m) => {
    if (m.id !== id) return m;
    touched = true;
    return { ...m, text: m.text + delta };
  });
  if (touched) {
    messages.value = next;
  } else {
    messages.value = [
      ...messages.value,
      { id, role: 'ai', text: delta, ts: Date.now(), streaming: true },
    ];
  }
}

/**
 * 数字人 delta 阶段:把流式增量累积到 fullText(不直接渲染);displayText 保持空,
 * 气泡显三点动画,等收到 ai.audio 再按时长逐字显出。若 id 不存在则补建 streaming AI 气泡。
 */
export function appendFullText(id: string, delta: string) {
  let touched = false;
  const next = messages.value.map((m) => {
    if (m.id !== id) return m;
    touched = true;
    return { ...m, fullText: (m.fullText ?? '') + delta };
  });
  if (touched) {
    messages.value = next;
  } else {
    messages.value = [
      ...messages.value,
      { id, role: 'ai', text: '', fullText: delta, ts: Date.now(), streaming: true },
    ];
  }
}

/** 数字人打字机推进:把已显出的文本写入 displayText(渲染层优先取 displayText)。 */
export function setDisplayText(id: string, shown: string) {
  updateMessage(id, { displayText: shown });
}

// ── 打字机输出(web 线) ─────────────────────────────────────────
/**
 * 打字机缓冲:dsh 侧 delta 按 150ms 合并成批到达,短回答常一两批就到齐,
 * 直接 appendDelta 上屏就是「整段弹出」。这里把 delta 先入队,按固定节奏逐字显出;
 * 积压越多每 tick 吐得越快,长回答不会越落越远。
 * digital 线不走这里(语音链路用 ai.audio 时长驱动自己的打字机,见 appendFullText)。
 */
const TYPEWRITER_TICK_MS = 40;
/** 每 tick 显出字符数:平稳期 2 字/tick(≈50字/s),积压大时按比例加速追上 */
function typewriterStep(backlog: number): number {
  return Math.max(2, Math.ceil(backlog / 25));
}

interface TypewriterEntry {
  /** 待显出文本 */
  pending: string;
  timer: ReturnType<typeof setInterval> | null;
  /** ai.stream.end 已到:缓冲吐完后才摘 streaming 标记 */
  ended: boolean;
}
const typewriterQueues = new Map<string, TypewriterEntry>();

function typewriterTick(id: string, entry: TypewriterEntry): void {
  if (!entry.pending) {
    if (entry.timer) {
      clearInterval(entry.timer);
      entry.timer = null;
    }
    if (entry.ended) {
      typewriterQueues.delete(id);
      updateMessage(id, { streaming: false });
      flushPendingLeadCardFor(id);
    }
    return;
  }
  // 消息已不在列表(会话重置/历史重建) → 丢弃缓冲,避免补建幽灵气泡
  if (!messages.value.some((m) => m.id === id)) {
    if (entry.timer) clearInterval(entry.timer);
    typewriterQueues.delete(id);
    return;
  }
  const n = typewriterStep(entry.pending.length);
  const chunk = entry.pending.slice(0, n);
  entry.pending = entry.pending.slice(n);
  appendDelta(id, chunk);
}

/** ai.stream.delta 入口(web 线):入打字机队列,按节奏显出 */
export function appendDeltaTypewriter(id: string, delta: string): void {
  let entry = typewriterQueues.get(id);
  if (!entry) {
    entry = { pending: '', timer: null, ended: false };
    typewriterQueues.set(id, entry);
  }
  entry.pending += delta;
  entry.timer ??= setInterval(
    () => typewriterTick(id, entry as TypewriterEntry),
    TYPEWRITER_TICK_MS,
  );
}

/** ai.stream.end 入口(web 线):标记回合结束;缓冲吐完后由 tick 摘 streaming */
export function finishDeltaTypewriter(id: string): void {
  const entry = typewriterQueues.get(id);
  if (!entry) {
    updateMessage(id, { streaming: false });
    flushPendingLeadCardFor(id);
    return;
  }
  entry.ended = true;
  if (!entry.pending && !entry.timer) {
    typewriterQueues.delete(id);
    updateMessage(id, { streaming: false });
    flushPendingLeadCardFor(id);
  }
}

// ── 留资卡弹卡闸门(web 线) ─────────────────────────────────────
/**
 * 待弹留资卡(signal,渲染层据此画「暂存期 pill」)。
 * lead.card.show 到达时,若锚消息还在流式输出(含打字机缓冲未吐完,streaming 未摘),
 * 先把卡暂存到这里,等该消息 streaming 落 false 才真正弹出 —— 避免卡片插在还在
 * 逐字生长的气泡下方(digital 线早有同款闸门,见 DigitalRoot pendingLeadCard;
 * 本条是 web 线的对齐实现)。暂存期间渲染层在锚消息下方给一个可点 pill,
 * 用户点击(openPendingLeadCard)可不等输出完直接展开。
 */
export const pendingLeadCard = signal<{
  /** flush 触发锚点:该消息 streaming 落 false 时弹卡;'' 表示待重绑(回合前置阶段) */
  anchorMsgId: string;
  data: LeadCardData;
} | null>(null);

/**
 * lead.card.show 统一入口(web 线):锚消息输出中则暂存,否则立即弹卡并落缓存。
 * 锚点缺失/锚消息不在当前列表(旧数据等)时直接弹,渲染层自行回落底部兜底。
 */
export function showLeadCard(data: LeadCardData): void {
  const anchor = data.anchorMsgId
    ? messages.value.find((m) => m.id === data.anchorMsgId)
    : undefined;
  if (anchor?.streaming) {
    pendingLeadCard.value = { anchorMsgId: data.anchorMsgId as string, data };
    return;
  }
  pendingLeadCard.value = null;
  activeLeadCard.value = data;
  lastLeadCard.value = data;
}

/** 某消息流式结束(streaming 落 false)时调用:暂存卡锚定它则真正弹卡 */
export function flushPendingLeadCardFor(messageId: string): void {
  const pending = pendingLeadCard.value;
  if (!pending || !pending.anchorMsgId || pending.anchorMsgId !== messageId) return;
  pendingLeadCard.value = null;
  activeLeadCard.value = pending.data;
  lastLeadCard.value = pending.data;
}

/** 暂存期 pill 点击:不等输出完立即展开,并清暂存(防打字完后 flush 重复弹出) */
export function openPendingLeadCard(): void {
  const pending = pendingLeadCard.value;
  if (!pending) return;
  pendingLeadCard.value = null;
  activeLeadCard.value = pending.data;
  lastLeadCard.value = pending.data;
}

/**
 * 历史卡还原但会话有在飞回合:不直接展开(会压在正在回复的内容上方),
 * 挂进暂存位等回合流式结束时 flush 弹出。锚点先置空——在飞消息 id 未知,
 * 由 restoreActiveTurn / ai.stream.start 拿到 id 后 rebindPendingLeadCard 重绑。
 */
export function deferLeadCardUntilTurnEnd(card: LeadCardData): void {
  lastLeadCard.value = card;
  pendingLeadCard.value = { anchorMsgId: '', data: card };
}

/** 在飞回合消息 id 确定后,重绑暂存卡的 flush 锚点(空锚才绑,不覆盖已绑定的) */
export function rebindPendingLeadCard(messageId: string): void {
  const pending = pendingLeadCard.value;
  if (pending && !pending.anchorMsgId) {
    pendingLeadCard.value = { ...pending, anchorMsgId: messageId };
  }
}

/**
 * 刷新 / SSE 断线重连后恢复在飞回合(BE turn.active 事件 / session.activeTurn 快照):
 *  - messageId 非空:补建(或复用)streaming AI 气泡并灌入已生成文本快照 ——
 *    语音链路(audioOn)灌 fullText 等 ai.audio 驱动打字机;非语音直接显文本,
 *    后续 ai.stream.delta 照常续接;
 *  - messageId 为 null:回合还在前置阶段(意图分类等),仅置「思考中」等 ai.stream.start。
 * 幂等:气泡已存在时只确保 streaming 标记,不重复灌文本(session 快照与 SSE 补帧可能都到)。
 * web(WebChatPanel)与 digital(DigitalRoot)共用,避免两条线行为漂移。
 */
export function restoreActiveTurn(
  turn: { messageId: string | null; text: string },
  opts?: { audioOn?: boolean },
): void {
  if (!turn.messageId) {
    aiThinking.value = true;
    return;
  }
  const existing = messages.value.find((m) => m.id === turn.messageId);
  if (existing) {
    updateMessage(turn.messageId, { streaming: true });
  } else if (opts?.audioOn) {
    appendMessage({
      id: turn.messageId,
      role: 'ai',
      text: '',
      fullText: turn.text,
      ts: Date.now(),
      streaming: true,
    });
  } else {
    appendMessage({
      id: turn.messageId,
      role: 'ai',
      text: turn.text,
      ts: Date.now(),
      streaming: true,
    });
  }
  // 有暂存的历史还原卡(空锚)时,把 flush 锚点重绑到这个在飞消息上,回合结束即弹出
  rebindPendingLeadCard(turn.messageId);
  aiThinking.value = false;
}

/**
 * 按当前接待模式过滤消息,实现 AI / 人工两条会话隔离:
 *  - 'human' 模式只显人工 IM 侧(channel==='human');
 *  - 'ai' 模式只显 AI 侧(channel 缺省或 'ai')。
 * web(WebChatPanel)与 digital(ChatPane)共用,保证两条线隔离行为一致。
 */
export function visibleMessages(all: UIMessage[], mode: 'ai' | 'human'): UIMessage[] {
  return all.filter((m) => (mode === 'human' ? m.channel === 'human' : m.channel !== 'human'));
}

export function toggleOpen() {
  opened.value = !opened.value;
}

export function setOpen(v: boolean) {
  opened.value = v;
}

/** digital 线:悬浮球点击 → 欢迎屏 */
export function openDigitalWelcome() {
  digitalView.value = 'welcome';
  opened.value = true;
}

/** digital 线:欢迎屏「立即咨询」/点引导问题 → 聊天 */
export function startDigitalChat() {
  digitalView.value = 'chat';
  opened.value = true;
}

/** digital 线:关闭按钮 → 回悬浮球。只收起 UI,不清会话/不断 SSE,再开仍是同一会话。 */
export function minimizeDigital() {
  digitalView.value = 'minimized';
  opened.value = false;
}

/**
 * 仅测试 / 重置场景使用。
 * 不重置 `config` / `ready` — 它们的生命周期与 widget bootstrap 绑定,
 * 测试中如需清理请显式 `config.value = null; ready.value = false;`
 */
export function resetStore() {
  // 打字机队列一并清空,防止重置后残留 tick 往新列表补建幽灵气泡
  for (const entry of typewriterQueues.values()) {
    if (entry.timer) clearInterval(entry.timer);
  }
  typewriterQueues.clear();
  pendingLeadCard.value = null;
  messages.value = [];
  inputDraft.value = '';
  quickQuestionsHidden.value = false;
  aiThinking.value = false;
  videoState.value = 'idle';
  activeLeadCard.value = null;
  lastLeadCard.value = null;
  digitalChatMode.value = 'ai';
  audioPlayingId.value = null;
}

/**
 * 用 /session 返回的 history + pendingCards 重建 UI 状态。
 *
 * - BE sender_type 映射: visitor→visitor, ai→ai, agent→human, system→system
 * - 跳过形如 "[工单已创建] ..." 的内部日志型 system 消息(BE appendSystem 写来给运维看的,
 *   不该展示给访客)
 * - 有历史时关掉快捷提问区,避免历史下方再露出 onboarding 卡
 * - pendingCards.lead.dismissed=true → 末尾追加 lead-reopen pill(可点重开卡)
 * - pendingCards.lead.dismissed=false → 直接把卡再亮出来(用户上次没交互)
 * - 后续工单卡等其它 kind 也按 pendingCards[kind] 分支扩展,无需改 schema。
 *
 * 重置/新会话场景下 history 为空、pendingCards 为 null 也照常工作。
 *
 * opts.deferLeadCard:会话有在飞回合(AI 还在回复)时传 true —— 未 dismiss 的还原卡
 * 不直接展开(会压在正在回复的内容上方),挂暂存位等回合流式结束再弹。
 */
export function hydrateFromHistory(
  history: SessionHistoryItem[],
  pendingCards: PendingCards | null,
  leadReopenHintText: string,
  opts?: { deferLeadCard?: boolean },
): void {
  const mapped: UIMessage[] = [];
  for (const h of history) {
    if (h.role === 'system' && h.content.startsWith('[')) continue;
    let role: UIMessage['role'];
    switch (h.role) {
      case 'visitor':
        role = 'visitor';
        break;
      case 'ai':
        role = 'ai';
        break;
      case 'agent':
        role = 'human';
        break;
      default:
        role = 'system';
    }
    mapped.push({
      id: h.id,
      role,
      // 人工坐席历史(sender=agent → role=human)归人工频道,只在人工模式展示;
      // 其余(visitor/ai/system)默认 AI 频道,与人工会话隔离。
      ...(role === 'human' ? { channel: 'human' as const } : {}),
      text: h.content,
      ts: new Date(h.createdAt).getTime(),
      // 历史 AI 消息已是完成态:直接把整段文本灌进 displayText/fullText。
      // 否则数字人启用语音时渲染层取 m.displayText(缺省为 undefined),
      // 历史回复会渲染成空气泡(看不到内容)。历史消息不再走打字机,直接整条显示。
      ...(role === 'ai' ? { displayText: h.content, fullText: h.content } : {}),
    });
  }

  // ── 留资卡 (kind='lead') 还原 ──
  // data 在容器里是 z.unknown(),按 LeadCardData 形状取字段
  const leadEntry = pendingCards?.['lead'];
  const leadData = leadEntry?.data as SharedLeadCardData | undefined;
  if (leadEntry?.dismissed) {
    mapped.push({
      id: `m-lead-reopen-resume-${Date.now()}`,
      role: 'system',
      text: leadReopenHintText,
      ts: Date.now(),
      // pill 锚定到触发卡片的 AI 回答正下方;老数据无 messageId 则缺省回落末尾
      meta: { kind: 'lead-reopen', anchorMsgId: leadData?.messageId },
    });
  }
  messages.value = mapped;
  if (mapped.length > 0) quickQuestionsHidden.value = true;

  if (leadEntry && leadData) {
    const card: LeadCardData = {
      fields: leadData.fields as LeadField[],
      prompt: leadData.prompt,
      // BE 持久化的触发消息 id:history 保留原始消息 id(h.id),锚点还原后仍生效;
      // 老数据无 messageId 时缺省,渲染层回落到消息流末尾。
      anchorMsgId: leadData.messageId,
    };
    lastLeadCard.value = card;
    if (leadEntry.dismissed) {
      activeLeadCard.value = null;
    } else if (opts?.deferLeadCard) {
      // 有在飞回合:不直接展开(会压在回复内容上方),暂存待回合结束再弹
      deferLeadCardUntilTurnEnd(card);
    } else {
      // dismissed=false 表示用户上次没关也没提交,直接重新弹卡
      activeLeadCard.value = card;
    }
  }

  // 后续 kind(如 'ticket')在此追加分支。
}
